/*
 * Copyright (c) 2023 Bluemate B.V.
 */

import React, {useEffect, useMemo, useState} from 'react';
import SystemService from '../../../models/systemServiceModel';
import {capitalize, ResponseData} from '@dvrd/dvr-controls';
import SystemServices from './systemServices';
import {onSetCrumbs} from '../../../util/events';
import {toggleAllServices} from '../../../util/requestSender';
import {SystemServiceName} from '../../../util/interfaces';
import {ADMIN_ROUTE, ADMIN_SERVICES_ROUTE} from '../../../util/constants';
import {showDialog, showSnackbar} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

export default function SystemServicesController() {
    const {t} = useTranslation();
    const [services, setServices] = useState<SystemService[]>([]);
    const [loading, setLoading] = useState(true);
    const someActive = useMemo(() => {
        return !!services.find(
            (service: SystemService) => service.name !== SystemServiceName.EMAIL_SEQUENCE && service.active
        );
    }, [services]);

    function onToggleAll() {
        const label = someActive ? t('admin.systemService.off', 'uit') : t('admin.systemService.onLabel', 'in');
        showDialog(
            `${t('admin.systemService.toggleAllConfirm1', 'Weet je zeker dat je alle services')} ${label} ${t('admin.systemService.toggleAllConfirm2', 'wilt schakelen?')}`,
            `${t('admin.systemService.allServices', 'Alle services')} ${label}${t('admin.systemService.switchSuffix', 'schakelen')}`,
            [
                t('admin.systemService.no', 'Nee'),
                {label: t('admin.systemService.yes', 'Ja'), onClick: onConfirmToggleAll, primary: false}
            ]
        );
    }

    function onConfirmToggleAll() {
        setLoading(true);
        toggleAllServices({
            data: {toggled: !someActive},
            callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success) {
                    const label = someActive
                        ? t('admin.systemService.off', 'uit')
                        : t('admin.systemService.onLabel', 'in');
                    setServices(data.services.map((service: any) => new SystemService(service)));
                    showSnackbar(
                        `${t('admin.systemService.allServicesAre', 'Alle services zijn')} ${label}${t('admin.systemService.switchedSuffix', 'geschakeld')}`
                    );
                } else
                    showDialog(
                        data.message ??
                            t('admin.systemService.changeFailedMsg', 'Het wijzigen van de services is niet gelukt.')
                    );
            }
        });
    }

    function onToggleService(service: SystemService) {
        return function () {
            const action = service.active
                ? t('admin.systemService.disableVerb', 'uitschakelen')
                : t('admin.systemService.enableVerb', 'inschakelen');
            showDialog(
                `${t('admin.systemService.toggleServiceConfirm', 'Weet je zeker dat je deze service wilt')} ${action}?`,
                t('admin.systemService.confirmTitle', 'Bevestig'),
                [
                    {
                        label: t('admin.systemService.no', 'Nee'),
                        primary: false
                    },
                    {label: t('admin.systemService.yes', 'Ja'), onClick: onConfirmToggleService(service)}
                ]
            );
        };
    }

    function onConfirmToggleService(service: SystemService) {
        return function () {
            setLoading(true);
            service.toggle((service: SystemService, success: boolean, data: ResponseData) => {
                setLoading(false);
                if (success) {
                    replaceService(service);
                    const action = service.active
                        ? t('admin.systemService.enabled', 'ingeschakeld')
                        : t('admin.systemService.disabled', 'uitgeschakeld');
                    showSnackbar(`${t('admin.systemService.serviceIs', 'De service is')} ${action}`);
                } else {
                    const action = service.active
                        ? t('admin.systemService.disableVerb', 'uitschakelen')
                        : t('admin.systemService.enableVerb', 'inschakelen');
                    showDialog(
                        data.message ||
                            `${t('admin.systemService.theWord', 'Het')} ${action} ${t('admin.systemService.ofServiceFailed', 'van de service is niet gelukt.')}`,
                        `${capitalize(action)} ${t('admin.systemService.failedSuffix', 'mislukt')}`
                    );
                }
            });
        };
    }

    function replaceService(service: SystemService) {
        const _services = services.slice();
        for (let i = 0; i < _services.length; i++) {
            if (_services[i].id === service.id) {
                _services.splice(i, 1, service);
                break;
            }
        }
        setServices(_services);
    }

    function loadServices() {
        SystemService.getAll((services: SystemService[]) => {
            setLoading(false);
            setServices(services);
        });
    }

    useEffect(() => {
        onSetCrumbs([
            {label: t('admin.systemService.crumb.admin', 'Admin'), route: ADMIN_ROUTE},
            {label: t('admin.systemService.crumb.services', 'Services'), route: ADMIN_SERVICES_ROUTE}
        ]);
        loadServices();
    }, []);

    return (
        <SystemServices
            onToggleService={onToggleService}
            onToggleAll={onToggleAll}
            services={services}
            loading={loading}
            someActive={someActive}
        />
    );
}
